export const AMPLITUDE_GLOBAL_PROPERTY = {
  FULL_URL: 'Full URL',
  HOSTNAME: 'Hostname',
  PAGE_NAME: 'Page Name',
  PAGE_PATH: 'Page Path',
  PAGE_TITLE: 'Page Title',
  LOGIN_STATUS: 'Login Status',
  PRODUCT: 'Product',
} as const;

export const AMPLITUDE_USER_PROPERTY = {
  USER_ID: 'User ID',
  JURISDICTION: 'Jurisdiction',
  NATIONAL_JURISDICTION: 'National Jurisdiction',
  SITE_PLATFORM: 'Site Platform',
  SITE_VERSION: 'Site Version',
  USER_LOCATION: 'User Location',
  ACCOUNT_ID: 'Account ID',
  DEVICE: 'Device',
  LAUNCHED_FROM: 'Launched From',
} as const;

export const AMPLITUDE_REST_PROPERTIES = {
  BRAND: 'Brand',
  ANDROID_DISTRIBUTION_METHOD: 'Android Distribution Method',
  BALANCE: 'Balance',
  STATE_SELECTED: 'State Selected',
  EVENT: 'Event',
  MODULE: 'Module',
  GAME_NAME: 'Game Name',
  POSITION: 'Position',
  GAME_ID: 'Game ID',
  IS_FAVORITE: 'Is Favorite',
  IS_JACKPOT: 'Is Jackpot',
  INTERACTION_TYPE: 'Interaction Type',
  FDC_JACKPOT_OPT_IN_STATUS: 'FDC Jackpot Opt In Status',
  COMPONENT: 'Component',
  GAME_PROVIDER: 'Game Provider',
  APP_NAME: 'App Name',
  APP_ID: 'App ID',
  APP_VERSION: 'App Version',
  ORIENTATION: 'Orientation',
  ZONE_NAME: 'Zone Name',
  LINK_URL: 'Link URL',
  LINK_TEXT: 'Link Text',
  TAG: 'Tag',
  BANNER_NAME: 'Banner Name',
  BANNER_TYPE: 'Banner Type',
  BANNER_ID: 'Banner ID',
  ERROR_TYPE: 'Error Type',
  POPUP_CURRENT_STEP: 'Popup Current Step',
  GAMING_SESSION_TRACKING_ID: 'Gaming Session Tracking ID',
  FDC_JACKPOT_WON_TIER: 'FDC Jackpot Tier Won',
  FDC_JACKPOT_WON_AMOUNT: 'FDC Jackpot Amount Won',
  FDC_JACKPOT_MULTIPLIER: 'FDC Jackpot Multiplier',
  MULTIPLIER: 'Multiplier',
  FDC_JACKPOT_MEGA_POT_AMOUNT: 'FDC Jackpot Mega Pot Amount',
  FDC_JACKPOT_MAJOR_POT_AMOUNT: 'FDC Jackpot Major Pot Amount',
  FDC_JACKPOT_MINOR_POT_AMOUNT: 'FDC Jackpot Minor Pot Amount',
  FDC_JACKPOT_MINI_POT_AMOUNT: 'FDC Jackpot Mini Pot Amount',
} as const;

export const USER_TRACKING_ALL_PROPS = {
  ...AMPLITUDE_USER_PROPERTY,
  ...AMPLITUDE_GLOBAL_PROPERTY,
  ...AMPLITUDE_REST_PROPERTIES,
} as const;

export const getAmplitudeKey = (
  key: keyof typeof USER_TRACKING_ALL_PROPS,
): string => USER_TRACKING_ALL_PROPS[key];

export const ZONES = {
  DASHBOARD: 'dashboard',
  IN_GAME: 'in_game',
  GAME_HELP: 'game_help',
} as const;

export const MODULES = {
  GW: 'game_window',
  GW_ERROR: 'game_window_error_modal',
  GW_EXIT_MODAL: 'game_window_exit_modal',
  IN_GAME: 'in_game',
  IN_GAME_HEADER: 'in_game_header',
  IN_GAME_FOOTER: 'in_game_footer',
  RECENTLY_PLAYED: 'in_game_recently_played',
  FEATURE_NOTIFICATION: 'feature_notification',
  JACKPOT_WIN: 'fdc_jackpots_win',
} as const;

export const AMPLITUDE_EVENTS = {
  SCREEN_LOAD: 'Screen Load',
  GAME_LAUNCH_INITIATED: 'Game Launch Initiated',
  GAME_LAUNCH_FAILED: 'Game Launch Failed',
  IN_GAME_MENU_OPENED: 'In Game Menu Opened',
  IN_GAME_EXITED: 'In Game Exited',
  IN_GAME_EXIT_MODAL_VIEWED: 'In Game Exit Modal Viewed',
  IN_GAME_EXIT_MODAL_CLOSED: 'In Game Exit Modal Closed',
  IN_GAME_ZONE_VIEWED: 'In Game Zone Viewed',
  IN_GAME_SOUND_ADJUSTED: 'In Game Sound Adjusted',
  IN_GAME_CTA_CLICKED: 'In Game CTA Clicked',
  NAVIGATION_LINK_CLICKED: 'Navigation Link Clicked',
  IN_GAME_NOTIFICATION_VIEWED: 'In Game Notification Viewed',
  IN_GAME_NOTIFICATION_SELECTED: 'In Game Notification Selected',
  IN_GAME_NOTIFICATION_CLOSED: 'In Game Notification Closed',
  IN_GAME_ERROR_MODAL_VIEWED: 'In Game Error Modal Viewed',
  IN_GAME_ERROR_CTA_CLICKED: 'In Game Error CTA Clicked',
  IN_GAME_JACKPOT_WIN: 'FDC Jackpot Won',
  IN_GAME_JACKPOT_MODAL_VIEWED: 'FDC Jackpots Modal Viewed',
  IN_GAME_JACKPOT_WIN_PAGE_CTA_CLICKED: 'FDC Jackpots Win Page CTA Clicked',
  IN_GAME_WIN_SCREEN_VIEWED: 'FDC Jackpots Win Page Viewed',
} as const;

export const TRACK_USER_ACTION_EVENT = {
  SCREEN_LOAD: 'SCREEN_LOAD',
  IN_GAME_EXITED: 'IN_GAME_EXITED',
  IN_GAME_EXIT_MODAL_VIEWED: 'IN_GAME_EXIT_MODAL_VIEWED',
  IN_GAME_EXIT_MODAL_CLOSED: 'IN_GAME_EXIT_MODAL_CLOSED',
  IN_GAME_MENU_OPENED: 'IN_GAME_MENU_OPENED',
  IN_GAME_SOUND_ADJUSTED: 'IN_GAME_SOUND_ADJUSTED',
  IN_GAME_ZONE_VIEWED: 'IN_GAME_ZONE_VIEWED',
  IN_GAME_CTA_CLICKED: 'IN_GAME_CTA_CLICKED',
  IN_GAME_ERROR_MODAL_VIEWED: 'IN_GAME_ERROR_MODAL_VIEWED',
  IN_GAME_ERROR_CTA_CLICKED: 'IN_GAME_ERROR_CTA_CLICKED',
  IN_GAME_NOTIFICATION_VIEWED: 'IN_GAME_NOTIFICATION_VIEWED',
  IN_GAME_NOTIFICATION_SELECTED: 'IN_GAME_NOTIFICATION_SELECTED',
  IN_GAME_NOTIFICATION_CLOSED: 'IN_GAME_NOTIFICATION_CLOSED',
  IN_GAME_SWJ_MIN_MAX_TOGGLE: 'IN_GAME_SWJ_MIN_MAX_TOGGLE',
  IN_GAME_JACKPOT_WIN: 'IN_GAME_JACKPOT_WIN',
  IN_GAME_JACKPOTS_MODAL_TOGGLED: 'IN_GAME_JACKPOTS_MODAL_TOGGLED',
  IN_GAME_JACKPOTS_MODAL_VIEWED: 'IN_GAME_JACKPOTS_MODAL_VIEWED',
  IN_GAME_JACKPOTS_OPT_IN_TOGGLED: 'IN_GAME_JACKPOTS_OPT_IN_TOGGLED',
  IN_GAME_SCREEN_TOGGLED: 'IN_GAME_SCREEN_TOGGLED',
  IN_GAME_WIN_SCREEN_CTA_CLICKED: 'IN_GAME_WIN_SCREEN_CTA_CLICKED',
  IN_GAME_WIN_SCREEN_VIEWED: 'IN_GAME_WIN_SCREEN_VIEWED',
} as const;

export const SOUND_INTERACTION_TYPE = {
  ON: 'on',
  OFF: 'off',
} as const;
